import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * ThemeContext — persists user's chosen theme in localStorage
 * and syncs it to `<html data-theme="…">` so CSS variables can
 * cascade to every element. Themes: "light" (default) or "dark".
 *
 * The dark theme keeps the same warm earthy accent palette
 * (green + amber) — just inverted surfaces — so shareable PNGs,
 * clay icons and the Sprout mascot stay visually consistent.
 */

const ThemeContext = createContext(null);
const STORAGE_KEY = "pfo_theme";

function readInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "dark" || saved === "light" || saved === "auto") return saved;
  } catch { /* localStorage unavailable */ }
  return "auto";
}

/** Resolve a stored preference (auto|light|dark) to the actually-applied theme. */
function resolveTheme(pref) {
  if (pref === "dark" || pref === "light") return pref;
  // auto — follow OS
  try {
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  } catch { /* SSR / older browser */ }
  return "light";
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readInitialTheme);
  const [theme, setThemeState] = useState(() => resolveTheme(readInitialTheme()));

  // Re-resolve when preference changes AND when OS scheme changes (auto only)
  useEffect(() => {
    setThemeState(resolveTheme(preference));
    if (preference !== "auto") return;
    let mq;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => setThemeState(resolveTheme("auto"));
      mq.addEventListener?.("change", handler);
      return () => mq.removeEventListener?.("change", handler);
    } catch { /* older browsers ignore */ }
  }, [preference]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.style.colorScheme = theme;
  }, [theme]);

  const setTheme = useCallback((next) => {
    setPreferenceState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  const toggleTheme = useCallback(() => {
    // Toggle skips "auto" so the user sees an instant flip
    setTheme(theme === "dark" ? "light" : "dark");
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={useMemo(
      () => ({ theme, preference, setTheme, toggleTheme }),
      [theme, preference, setTheme, toggleTheme],
    )}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
};
